import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useOutlet } from 'react-router-dom'
import { KeepAlive, useKeepAliveRef } from 'keepalive-for-react'
import { useTabStore } from '../../store/tabStore'
import { usePageRefreshStore } from '../../store/pageRefreshStore'
import {
  keepAliveCacheKey,
  keepAliveTabIdFromCacheKey,
  registerKeepAliveBridge,
} from './keepAliveBridge'
import { isTailscaleBrowserPage } from '../../services/apiConfig'

const matchesCacheKey = (cacheKey: string, match: string | RegExp) =>
  typeof match === 'string' ? cacheKey.includes(match) : match.test(cacheKey)

/** Live page trees in RAM. Tab titles can stay open; older panes remount on click. */
const MAX_KEEP_ALIVE_PANES = 8

/**
 * Pages that must unmount when left: always-fresh forms, or heavy polling UIs
 * that keep firing requests while hidden and freeze the browser after many tabs.
 */
const KEEP_ALIVE_EXCLUDE = [
  /::\/invoices(\?|$|::)/,
  /::\/invoices\/new(\?|$|::)/,
  /::\/purchase-invoices(\?|$|::)/,
  /::\/opening-stock-invoices\/new(\?|$|::)/,
  /::\/pos(\?|$|::)/,
  /::\/kitchen(\?|$|::)/,
  /::\/restaurant\/orders-queue(\?|$|::)/,
  /::\/signals(\?|$|::)/,
  /::\/fuel-station(\?|$|::)/,
  /::\/shifts(\?|$|::)/,
  /::\/workflows(\?|$|::)/,
  /::\/notifications(\?|$|::)/,
  /::\/settings\/connected-devices(\?|$|::)/,
  /::\/tailscale-devices(\?|$|::)/,
  /::\/settings\/tailscale-devices(\?|$|::)/,
  /::\/hr\/attendance-app(\?|$|::)/,
  /::\/admin\/operations(\?|$|::)/,
]

/**
 * Caches each open tab's route tree in memory (RAM).
 * Switching tabs only hides/shows panes — no remount, no browser storage.
 * Closing a tab (or logout) destroys that tab's cached panes.
 *
 * Inactive panes stay mounted and keep running queries/effects, so the cache
 * must stay small. ~10–20 full ERP pages (invoices, items, …) exhausts the
 * browser (memory + HTTP sockets) and new menu clicks appear to do nothing.
 */
export default function KeepAliveOutlet() {
  const outlet = useOutlet()
  const location = useLocation()
  const aliveRef = useKeepAliveRef()
  const tabs = useTabStore((s) => s.tabs)
  const activeTabId = useTabStore((s) => s.activeTabId)
  const refreshGeneration = usePageRefreshStore((s) => s.generations[activeTabId] ?? 0)

  const activeCacheKey = useMemo(
    () => `${keepAliveCacheKey(activeTabId, location.pathname)}::r${refreshGeneration}`,
    [activeTabId, location.pathname, refreshGeneration]
  )

  const tabIdsKey = useMemo(() => tabs.map((t) => t.id).sort().join('|'), [tabs])
  const prevTabIdsRef = useRef<Set<string>>(new Set(tabs.map((t) => t.id)))

  useEffect(() => {
    registerKeepAliveBridge({
      destroyByTabId: (tabId) => {
        const nodes = aliveRef.current?.getCacheNodes?.() ?? []
        const keys = nodes
          .map((n) => n.cacheKey)
          .filter((k) => keepAliveTabIdFromCacheKey(k) === tabId)
        if (keys.length > 0) {
          void aliveRef.current?.destroy(keys)
        }
      },
      destroyMatching: (match) => {
        const nodes = aliveRef.current?.getCacheNodes?.() ?? []
        const keys = nodes
          .map((n) => n.cacheKey)
          .filter((k) => matchesCacheKey(k, match))
        if (keys.length > 0) {
          void aliveRef.current?.destroy(keys)
        }
      },
      destroyAll: () => {
        void aliveRef.current?.destroyAll()
      },
    })
    return () => registerKeepAliveBridge(null)
  }, [aliveRef])

  // When tabs are closed (single or bulk), drop their cached panes from RAM.
  useEffect(() => {
    const current = new Set(tabs.map((t) => t.id))
    const prev = prevTabIdsRef.current
    const closed: string[] = []
    for (const id of prev) {
      if (!current.has(id)) closed.push(id)
    }
    if (closed.length > 0) {
      const nodes = aliveRef.current?.getCacheNodes?.() ?? []
      const keys = nodes
        .map((n) => n.cacheKey)
        .filter((k) => closed.includes(keepAliveTabIdFromCacheKey(k)))
      if (keys.length > 0) {
        void aliveRef.current?.destroy(keys)
      }
    }
    prevTabIdsRef.current = current
  }, [tabIdsKey, tabs, aliveRef])

  // One live tree per tab. List → detail (or any intra-tab route) used to leave
  // the previous route mounted, so 10 tabs quickly became 20–30 full pages.
  useEffect(() => {
    const nodes = aliveRef.current?.getCacheNodes?.() ?? []
    const tabId = keepAliveTabIdFromCacheKey(activeCacheKey)
    const stale = nodes
      .map((n) => n.cacheKey)
      .filter((k) => k !== activeCacheKey && keepAliveTabIdFromCacheKey(k) === tabId)
    if (stale.length > 0) {
      void aliveRef.current?.destroy(stale)
    }
  }, [activeCacheKey, aliveRef])

  return (
    <KeepAlive
      aliveRef={aliveRef}
      activeCacheKey={activeCacheKey}
      max={isTailscaleBrowserPage() ? 3 : MAX_KEEP_ALIVE_PANES}
      exclude={KEEP_ALIVE_EXCLUDE}
      containerClassName="relative h-full keep-alive-outlet"
      cacheNodeClassName="keep-alive-pane"
    >
      {outlet}
    </KeepAlive>
  )
}
